// [2026-09-02] ADDED — tenants: the screen an operator spends their day on.
//
// It is the ONLY place in the product where a company is onboarded, given credits, issued a
// credential, or switched off. Every one of those is a write that a customer feels immediately, so
// each is confirmed, each states its consequence in the confirmation, and each is written to the
// audit log server-side with the operator's name on it.
//
// THREE THINGS THIS SCREEN IS CAREFUL ABOUT
//
// 1. A NEW SECRET IS SHOWN EXACTLY ONCE. `issueKey` is the only call here whose response contains a
//    live secret. It is rendered into a panel and never stored, cached or logged — losing it means
//    issuing another key, which is cheap, and is a far better failure than a recoverable secret.
//
// 2. REVOKE AND SUSPEND ARE NOT INSTANT, AND THE UI SAYS SO. The backend caches key lookups
//    (GATEWAY_API_KEY_CACHE_TTL_SECONDS), so a revoked key can keep authenticating for up to that
//    window. An operator revoking a leaked credential needs to know that, not discover it.
//
// 3. ENV-CONFIGURED TENANTS CANNOT BE EDITED. Clients from GATEWAY_CLIENTS_JSON come back with
//    `editable: false` and `source: "env"` — their status lives in a deployment's environment, not
//    in Mongo, so the suspend control is disabled for them rather than failing at the API.

import { useState } from 'react'
import {
  Card, Empty, Modal, Notice, Skeleton, Tile, num, ms, pct, when, toast, useAsync,
} from '@incruiter/proctoring-client-frontend'
import { TrafficChart } from '@incruiter/proctoring-client-frontend'
import { Badge } from '@incruiter/proctoring-client-frontend/src/components/ui/badge.jsx'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@incruiter/proctoring-client-frontend/src/components/ui/table.jsx'
import { admin } from '../api.js'
// [2026-09-07] ADDED — the in-app dialog that replaced window.prompt/confirm on this screen.
// See ask.jsx: these are the controls that move money and suspend live tenants, and a browser
// dialog saying "dev.admin.v1.proctor.incruiter.com says" is the wrong frame for that.
import { useAsk, positiveNumber, nonZeroNumber, required } from '../ask.jsx'

const STATUS_VARIANT = { active: 'healthy', suspended: 'critical', disabled: 'critical' }

// [2026-09-23] ADDED — the two kinds of key a customer holds, named the way THEIR dashboard names
// them (proctoring-client-frontend PlatformKeys.jsx: "SDK Keys" / "API Keys"). `service` is the
// value on the wire and must match app/core/apikeys.py:SERVICES exactly; anything else is a 422.
// A row with no `service` is an SDK key — the server reads it the same way (apikeys.list_keys).
const KEY_KIND = {
  sdk: 'SDK key',
  proctoring: 'API key',
}
const kindOf = (service) => KEY_KIND[service || 'sdk'] || service

/** The one-time reveal of a freshly issued credential. */
function SecretPanel({ secret }) {
  const copy = (text, what) => {
    navigator.clipboard?.writeText(text).then(() => toast(`${what} copied`))
      .catch(() => toast('Copy failed — select and copy manually', true))
  }
  return (
    <div style={{ border: '1px solid var(--good, #1f4a29)', borderRadius: 'var(--radius-sm)',
                  padding: '12px 14px', marginBottom: 12, background: 'var(--good-bg)' }}>
      {/* [2026-09-23] CHANGED — names WHICH kind of key this is. With two issue buttons, an
          operator handing over two secrets in a row must be able to tell them apart.
          OLD: <b style={{ fontSize: 13 }}>New key — hand these to the customer now. The secret is shown once.</b> */}
      <b style={{ fontSize: 13 }}>
        New {kindOf(secret.service)} — hand these to the customer now. The secret is shown once.
      </b>
      {[['X-API-Key', secret.api_key], ['X-API-Secret', secret.secret]].map(([label, value]) => (
        <div key={label} style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
          <span className="muted" style={{ fontSize: 11.5, width: 96 }}>{label}</span>
          <input readOnly value={value} className="mono" style={{ flex: 1 }} />
          <button className="btn ghost" onClick={() => copy(value, label)}>Copy</button>
        </div>
      ))}
    </div>
  )
}

/** Everything about one tenant, plus the four writes an operator performs on it. */
function ClientDetail({ clientId, onClose, onChanged }) {
  const [nonce, setNonce] = useState(0)
  const { data, error, loading } = useAsync(() => admin.client(clientId), [clientId, nonce])
  const [secret, setSecret] = useState(null)
  const [busy, setBusy] = useState(false)
  // [2026-09-07] ADDED — `ask` is awaited by every write below; `askDialog` is the rendered dialog
  // and must appear in this component's tree or nothing is drawn. See ask.jsx.
  const [ask, askDialog] = useAsk()

  const reload = () => { setNonce((n) => n + 1); onChanged?.() }

  // [2026-09-07] CHANGED — one dialog, was two stacked window.prompt calls.
  //
  // OLD: prompt for the amount, validate it AFTER the second prompt was answered, prompt for the
  // reason, and cancelling the second silently discarded the first. Typing "abc" cost two dialogs
  // before a toast said it was wrong. Both questions belong to one decision, so they are now one
  // dialog and the amount is validated in place, under the field, while it is still editable.
  async function grant() {
    const v = await ask({
      title: 'Grant credits',
      subtitle: clientId,
      tone: 'primary',
      confirmLabel: 'Grant credits',
      fields: [
        { name: 'credits', label: 'Credits', type: 'number', defaultValue: '100',
          validate: positiveNumber() },
        { name: 'reason', label: 'Reason', defaultValue: 'top-up',
          hint: 'Written to the ledger — there is no way to move money without saying why.',
          validate: required('A reason') },
      ],
    })
    if (!v) return
    const credits = Number(v.credits)
    const reason = v.reason
    setBusy(true)
    try {
      await admin.grant(clientId, credits, reason)
      toast(`${credits} credits granted`)
      reload()
    } catch (err) { toast(err.message, true) } finally { setBusy(false) }
  }

  // [2026-09-07] CHANGED — one dialog. Same reasoning as grant() above.
  async function adjust() {
    const v = await ask({
      title: 'Correct balance',
      subtitle: clientId,
      tone: 'primary',
      confirmLabel: 'Apply correction',
      fields: [
        { name: 'credits', label: 'Correction', type: 'number', defaultValue: '-10',
          hint: 'Negative removes credits, positive adds them.', validate: nonZeroNumber },
        { name: 'reason', label: 'Reason', defaultValue: 'billing correction',
          validate: required('A reason') },
      ],
    })
    if (!v) return
    const credits = Number(v.credits)
    const reason = v.reason
    setBusy(true)
    try {
      await admin.adjust(clientId, credits, reason)
      toast('Balance corrected')
      reload()
    } catch (err) { toast(err.message, true) } finally { setBusy(false) }
  }

  // [2026-09-07] CHANGED — OLD: window.prompt for the label.
  // [2026-09-23] CHANGED — takes the KIND of key. This screen could only ever issue an SDK key:
  // it sent no `service`, and the server defaults a missing one to 'sdk'. A customer's own
  // dashboard lists SDK keys and API keys separately, so an operator onboarding them by hand had
  // no way to issue the second one.
  //
  // OLD:
  // async function issueKey() {
  //   const v = await ask({
  //     title: 'Issue an API key',
  //     subtitle: clientId,
  //     tone: 'primary',
  //     confirmLabel: 'Issue key',
  //     fields: [
  //       { name: 'label', label: 'Label', defaultValue: 'prod backend',
  //         hint: 'Shown when listing keys; never used for authentication.' },
  //     ],
  //   })
  //   if (!v) return
  //   const label = v.label
  //   setBusy(true)
  //   try {
  //     setSecret(await admin.issueKey(clientId, label))
  //     toast('Key issued')
  //     reload()
  //   } catch (err) { toast(err.message, true) } finally { setBusy(false) }
  // }
  async function issueKey(service) {
    const kind = kindOf(service)
    const v = await ask({
      title: `Issue an ${kind}`,
      subtitle: clientId,
      tone: 'primary',
      confirmLabel: `Issue ${kind}`,
      fields: [
        { name: 'label', label: 'Label', defaultValue: `${kind}`,
          hint: 'Shown when listing keys; never used for authentication.' },
      ],
    })
    if (!v) return
    const label = v.label
    setBusy(true)
    try {
      setSecret(await admin.issueKey(clientId, label, service))
      toast(`${kind} issued`)
      reload()
    } catch (err) { toast(err.message, true) } finally { setBusy(false) }
  }

  async function revokeKey(prefix) {
    // [2026-09-07] CHANGED — OLD: window.confirm with the same two sentences.
    if (!(await ask({
      title: `Revoke ${prefix}?`,
      subtitle: clientId,
      confirmLabel: 'Revoke key',
      message: 'Anything using it stops working — though the backend caches key lookups, so it '
               + 'can keep working for up to the cache TTL.',
    }))) return
    setBusy(true)
    try {
      await admin.revokeKey(clientId, prefix, 'revoked from admin dashboard')
      toast('Key revoked')
      reload()
    } catch (err) { toast(err.message, true) } finally { setBusy(false) }
  }

  async function toggleStatus(next) {
    const verb = next === 'suspended' ? 'Suspend' : 'Reactivate'
    // [2026-09-07] CHANGED — one dialog, was a confirm followed by a prompt. Cancelling the old
    // second dialog left the operator having already agreed to suspend a live tenant and then
    // silently not doing it, which is a worse outcome than either answer.
    const v = await ask({
      title: `${verb} ${clientId}?`,
      confirmLabel: verb,
      tone: next === 'suspended' ? 'danger' : 'primary',
      message: next === 'suspended'
        ? 'Every one of their API keys stops authenticating. Live exams will fail.'
        : 'Their keys start working again.',
      fields: [
        { name: 'reason', label: 'Reason',
          defaultValue: next === 'suspended' ? 'non-payment' : 'resolved',
          hint: 'Written to the audit log.', validate: required('A reason') },
      ],
    })
    if (!v) return
    const reason = v.reason
    setBusy(true)
    try {
      await admin.setClientStatus(clientId, next, reason)
      toast(next === 'suspended' ? 'Tenant suspended' : 'Tenant reactivated')
      reload()
    } catch (err) { toast(err.message, true) } finally { setBusy(false) }
  }

  // [2026-09-07] ADDED — switch a tenant's products.
  //
  // NO OPTIMISTIC UPDATE, deliberately. The server applies the dependency rule (turning AI
  // Proctoring on turns the SDK on with it; turning the SDK off turns Proctoring off), so the
  // resulting pair is not always what the clicked switch implies. Rendering the assumed value and
  // correcting it a moment later would show the operator a state that never existed. `reload()`
  // takes the authoritative answer instead.
  //
  // The confirm names the CONSEQUENCE rather than the field: an operator is deciding what a
  // customer is billed for, not editing a flag.
  async function setProduct(product, next) {
    const label = product === 'ai_proctoring' ? 'AI Proctoring' : 'Browser SDK'
    const consequence = next
      ? (product === 'ai_proctoring'
         ? 'Their recordings can be submitted for analysis, and the Browser SDK is switched on too '
           + '(we do not sell proctoring on its own). One interview costs one credit, charged only '
           + 'when BOTH halves succeed.'
         : 'They can mint candidate sessions. One interview costs one credit.')
      : (product === 'sdk'
         ? 'Candidate sessions stop being minted, and AI Proctoring is switched off with them.'
         : 'Video analysis is refused for this tenant. Candidate sessions are unaffected.')
    // [2026-09-07] CHANGED — OLD: window.confirm with the same title and consequence.
    if (!(await ask({
      title: `${next ? 'Enable' : 'Disable'} ${label}?`,
      subtitle: clientId,
      confirmLabel: next ? 'Enable' : 'Disable',
      tone: next ? 'primary' : 'danger',
      message: consequence,
    }))) return
    setBusy(true)
    try {
      const res = await admin.setClientProduct(clientId, product, next)
      toast(`${label} ${next ? 'enabled' : 'disabled'}`
            + (res?.effective_within_seconds
               ? ` — live within ${res.effective_within_seconds}s` : ''))
      reload()
    } catch (err) { toast(err.message, true) } finally { setBusy(false) }
  }

  const c = data?.client || {}
  const credits = data?.credits || {}
  const usage = data?.usage || {}
  const keys = data?.keys || []
  const editable = c.editable !== false
  // ABSENT ai_proctoring MEANS OFF — it is opt-in, unlike the other toggles on the client record.
  // A new tenant provisions itself with no products set, so this must not read as "enabled".
  const services = c.services || {}
  const sdkOn = services.sdk !== false
  const proctoringOn = services.ai_proctoring === true

  return (
    <Modal wide title={c.name || clientId}
           subtitle={`${clientId}${c.platform_code ? ` · ${c.platform_code}` : ''}`}
           onClose={onClose}>
      {/* [2026-09-07] ADDED — the in-app dialog. Rendered ALONGSIDE the drawer's content rather
          than inside a branch, so it survives the drawer re-rendering while a write is in flight. */}
      {askDialog}
      {loading && <Skeleton height={220} />}
      {error && <Notice kind="error">{error.message}</Notice>}
      {data && (
        <div style={{ display: 'grid', gap: 14 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <Badge variant={STATUS_VARIANT[c.status] || 'idle'}>{c.status}</Badge>
            <Badge variant="outline">source: {c.source}</Badge>
            {c.contact_email
              ? <span className="mono" style={{ fontSize: 12.5 }}>{c.contact_email}</span>
              : <span className="muted" style={{ fontSize: 12.5, fontStyle: 'italic' }}>
                  no email on record
                </span>}
          </div>

          <div style={{ display: 'grid', gap: 10,
                        gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))' }}>
            <Tile label="Balance" value={num(credits.balance_credits || 0, { decimals: 2 })}
                  tone={credits.low_balance ? 'var(--critical)' : undefined} />
            <Tile label="Granted" value={num(credits.total_granted_credits || 0, { decimals: 0 })} />
            <Tile label="Spent" value={num(credits.total_spent_credits || 0, { decimals: 2 })} />
            <Tile label="Requests" value={num(usage.total_calls || 0)} />
            <Tile label="Error rate" value={pct(usage.error_rate || 0)} />
            <Tile label="Sessions" value={num((data.session_counts || {}).sessions || 0)} />
          </div>

          {/* ── the four writes ─────────────────────────────────────────── */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="btn" disabled={busy} onClick={grant}>+ Grant credits</button>
            <button className="btn ghost" disabled={busy} onClick={adjust}>Correct balance</button>
            {/* [2026-09-23] CHANGED — one button per kind of key; see issueKey above.
                OLD: <button className="btn ghost" disabled={busy} onClick={issueKey}>Issue API key</button>
                That button issued an SDK key despite its label. */}
            <button className="btn ghost" disabled={busy} onClick={() => issueKey('sdk')}>
              Issue SDK key
            </button>
            <button className="btn ghost" disabled={busy} onClick={() => issueKey('proctoring')}>
              Issue API key
            </button>
            {editable ? (
              <button className="btn ghost" disabled={busy}
                      onClick={() => toggleStatus(c.status === 'active' ? 'suspended' : 'active')}>
                {c.status === 'active' ? 'Suspend tenant' : 'Reactivate tenant'}
              </button>
            ) : (
              // Not hidden — disabled and explained. A missing control reads as a missing feature.
              <button className="btn ghost" disabled
                      title="This tenant comes from GATEWAY_CLIENTS_JSON; its status lives in the deployment's environment, not in the database.">
                Suspend (env tenant — not editable)
              </button>
            )}
          </div>

          {/* ── products ─────────────────────────────────────────────────
              WHAT THIS TENANT IS SOLD, and therefore how they are billed. Two switches, not three
              options, because the third combination does not exist: proctoring requires the SDK,
              and the server enforces that whichever switch is clicked. */}
          <div style={{ border: '1px solid var(--line)', borderRadius: 10, padding: '12px 14px' }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>Products</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 10 }}>
              One interview costs one credit. With AI Proctoring on it is charged only when the
              session <em>and</em> the analysis both succeed.
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <button className={sdkOn ? 'btn' : 'btn ghost'} disabled={busy || !editable}
                      onClick={() => setProduct('sdk', !sdkOn)}>
                {sdkOn ? '\u2713 ' : ''}Browser SDK
              </button>
              <button className={proctoringOn ? 'btn' : 'btn ghost'} disabled={busy || !editable}
                      onClick={() => setProduct('ai_proctoring', !proctoringOn)}>
                {proctoringOn ? '\u2713 ' : ''}AI Proctoring
              </button>
              <span style={{ fontSize: 12, color: 'var(--muted)' }}>
                {proctoringOn ? 'Plan: SDK + AI Proctoring'
                              : sdkOn ? 'Plan: SDK only' : 'No products enabled'}
              </span>
            </div>
          </div>

          {secret && <SecretPanel secret={secret} />}

          <Card title="API keys" subtitle={`${data.keys_active ?? keys.filter((k) => k.status === 'active').length} active`}>
            {keys.length === 0 ? (
              // [2026-09-02] The empty state distinguishes "no keys" from "no MANAGEABLE keys".
              // GET /clients/{id}/keys returns runtime keys from Mongo only; an env tenant's
              // credential lives in GATEWAY_CLIENTS_JSON and is not listed. Saying "No keys" for a
              // tenant that is authenticating traffic right now is simply false, and would send an
              // operator to issue a second key for a company that already has one.
              <Empty
                title={editable ? 'No keys' : 'No runtime keys'}
                hint={editable
                  ? 'Issue one with the button above.'
                  : 'This tenant is configured in GATEWAY_CLIENTS_JSON — its credential lives in the '
                    + 'deployment environment and is not listed or revocable here. Issuing a key '
                    + 'below adds a runtime one alongside it.'} />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    {/* [2026-09-23] ADDED — the Type column. With two kinds issued here, a list
                        that does not say which is which cannot answer "does this customer have
                        their API key yet?". */}
                    <TableHead>Key</TableHead><TableHead>Type</TableHead><TableHead>Label</TableHead>
                    <TableHead>Status</TableHead><TableHead>Created</TableHead>
                    <TableHead style={{ textAlign: 'right' }}>Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {keys.map((k) => (
                    <TableRow key={k.key_prefix}>
                      <TableCell className="mono">{k.key_prefix}</TableCell>
                      {/* [2026-09-23] ADDED — see the Type header above. */}
                      <TableCell>{kindOf(k.service)}</TableCell>
                      <TableCell>{k.label || <span className="muted">—</span>}</TableCell>
                      <TableCell>
                        <Badge variant={k.status === 'active' ? 'healthy' : 'idle'}>{k.status}</Badge>
                      </TableCell>
                      <TableCell className="muted">
                        {k.created_at ? new Date(k.created_at * 1000).toLocaleString() : '—'}
                      </TableCell>
                      <TableCell style={{ textAlign: 'right' }}>
                        {k.status === 'active'
                          ? <button className="btn ghost" disabled={busy}
                                    onClick={() => revokeKey(k.key_prefix)}>Revoke</button>
                          : <span className="muted" style={{ fontSize: 12 }}>{k.revoked_reason || '—'}</span>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>

          {(data.requests_series || []).length > 0 && (
            <Card title="Traffic" subtitle={`Last ${data.window_days} days`}>
              <TrafficChart data={data.requests_series} />
            </Card>
          )}

          <Card title="Ledger" subtitle="Newest first — every movement of this tenant's money">
            {(data.ledger || []).length === 0 ? <Empty title="No movements yet" hint="" /> : (
              <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>When</TableHead><TableHead>Type</TableHead>
                      <TableHead style={{ textAlign: 'right' }}>Change</TableHead>
                      <TableHead>Reason</TableHead><TableHead>Actor</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.ledger.map((e) => {
                      const delta = (e.amount_milli || 0) / 1000
                      return (
                        <TableRow key={e.entry_id}>
                          <TableCell className="muted">{when(e.created_at)}</TableCell>
                          <TableCell>{e.type}</TableCell>
                          <TableCell style={{ textAlign: 'right',
                                              color: delta >= 0 ? 'var(--good)' : 'var(--critical)' }}>
                            {delta >= 0 ? '+' : ''}{num(delta, { decimals: 3 })}
                          </TableCell>
                          <TableCell className="muted">{e.reason || '—'}</TableCell>
                          <TableCell className="muted">{e.actor || '—'}</TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </Card>
        </div>
      )}
    </Modal>
  )
}

/** Onboard a company. Three single-purpose steps follow it (key, credits, activate) rather than one
 *  that half-succeeds — so this form deliberately only creates the tenant. */
function OnboardModal({ onClose, onCreated }) {
  const [name, setName] = useState('')
  const [origin, setOrigin] = useState('')
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  async function submit(event) {
    event.preventDefault()
    if (!name.trim()) { setError('A company name is required.'); return }
    setBusy(true)
    setError(null)
    try {
      const created = await admin.createClient({
        name: name.trim(),
        origin: origin.trim() || undefined,
        contact_email: email.trim() || undefined,
      })
      toast(`Created ${created.client_id}`)
      onCreated(created.client_id)
    } catch (err) { setError(err.message) } finally { setBusy(false) }
  }

  return (
    <Modal title="Onboard a company"
           subtitle="Creates the tenant only. Issue a key and grant credits from its page next."
           onClose={onClose}>
      {error && <Notice kind="error">{error}</Notice>}
      <form onSubmit={submit}>
        <label className="field"><span>Company name</span>
          <input value={name} autoFocus onChange={(e) => setName(e.target.value)}
                 placeholder="Acme Corp" /></label>
        <label className="field"><span>Exam origin (optional)</span>
          <input value={origin} onChange={(e) => setOrigin(e.target.value)}
                 placeholder="https://exams.acme.com" /></label>
        <label className="field"><span>Contact email (optional)</span>
          <input value={email} onChange={(e) => setEmail(e.target.value)}
                 placeholder="ops@acme.com" /></label>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
          <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn" disabled={busy}>{busy ? 'Creating…' : 'Create tenant'}</button>
        </div>
      </form>
    </Modal>
  )
}

export default function Clients({ days = 30, onError, openClientId, onCloseClient }) {
  const [nonce, setNonce] = useState(0)
  const { data, error, loading } = useAsync(() => admin.clients(days), [days, nonce])
  const [open, setOpen] = useState(null)
  const [onboarding, setOnboarding] = useState(false)
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')

  // The overview screen deep-links into a tenant; honour it without making it this screen's state.
  const detailId = open || openClientId

  if (error) { onError?.(error); return <Notice kind="error">{error.message}</Notice> }
  // [2026-09-23] CHANGED — the skeleton is for the FIRST load only.
  //
  // OLD: if (loading || !data) return <Skeleton height={280} />
  //
  // THE BUG. useAsync keeps the previous `data` while it refetches and only flips `loading` back to
  // true (proctoring-client-frontend src/ui.jsx:useAsync). Every write in ClientDetail calls
  // onChanged(), which refetches this list — and this line then replaced the WHOLE screen,
  // including the open ClientDetail below, with a skeleton. That unmounted ClientDetail and threw
  // away its state. For "Issue API key" that state is the one-time secret: the key was created,
  // the server returned the secret once, and the operator never saw it. Reported on test,
  // 2026-09-23, while onboarding Hiresense.
  //
  // `loading` stays in the destructure because nothing else reads it and removing it is not this
  // change's business; the stale list stays on screen for the half-second the refetch takes.
  if (!data) return <Skeleton height={280} />

  const term = search.trim().toLowerCase()
  const rows = (data.clients || []).filter((c) => {
    if (status && c.status !== status) return false
    if (!term) return true
    return [c.client_id, c.name, c.contact_email, c.domain, c.platform_code]
      .filter(Boolean).some((v) => String(v).toLowerCase().includes(term))
  })

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {data.runtime_onboarding_enabled === false && (
        <Notice kind="warn">
          Runtime onboarding is disabled in this environment (GATEWAY_DB_CLIENTS_ENABLED) — new
          tenants cannot be created here.
        </Notice>
      )}

      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)',
                    borderRadius: 'var(--radius)', padding: '18px 20px' }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center',
                      justifyContent: 'space-between', marginBottom: 14 }}>
          <div>
            <div style={{ fontWeight: 600 }}>Tenants</div>
            <div className="muted" style={{ fontSize: 12.5, marginTop: 2 }}>
              {rows.length} of {data.count} · search by email, then open the row to grant credits
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {/* CLIENT-SIDE SEARCH HERE, unlike every other table in this app — and that is a
                deliberate exception, not an inconsistency. GET /admin/v1/clients returns the whole
                tenant list in one response (there are tens, not thousands, and each row is already
                a rollup), so there is no page to fetch and nothing to be stale about. */}
            <input value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search"
                   placeholder="Search email, company or id…" style={{ minWidth: 220 }} />
            <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status"
                    style={{ padding: '9px 10px', borderRadius: 'var(--radius-sm)',
                             border: '1px solid var(--border)', background: 'var(--surface)',
                             color: 'inherit', font: 'inherit' }}>
              <option value="">Status: all</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
            </select>
            <button className="btn" onClick={() => setOnboarding(true)}>+ Onboard company</button>
          </div>
        </div>

        {rows.length === 0 ? (
          <Empty title="No tenants match" hint="Clear the search or onboard a company." />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tenant</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead style={{ textAlign: 'right' }}>Balance</TableHead>
                  <TableHead style={{ textAlign: 'right' }}>Requests</TableHead>
                  <TableHead style={{ textAlign: 'right' }}>Errors</TableHead>
                  <TableHead style={{ textAlign: 'right' }}>Sessions</TableHead>
                  <TableHead>Last active</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((c) => (
                  <TableRow key={c.client_id} style={{ cursor: 'pointer' }}
                            onClick={() => setOpen(c.client_id)}>
                    {/* [2026-09-03] The EMAIL is shown, not just the generated client_id.
                        A tenant arrives here through SSO, so its client_id is a slug nobody
                        recognises (`incruiter-c09b`) while the email is the thing an operator was
                        given in the request to add credits. The search box below already matched on
                        contact_email — it simply could not be READ off the screen, so nobody knew
                        they could search by it. */}
                    <TableCell>
                      <div>{c.name || c.client_id}</div>
                      {c.contact_email
                        ? <div className="muted" style={{ fontSize: 12 }}>{c.contact_email}</div>
                        : <div className="muted" style={{ fontSize: 11.5, fontStyle: 'italic' }}>
                            no email on record
                          </div>}
                      <div className="muted mono" style={{ fontSize: 11 }}>
                        {c.client_id}{c.platform_code ? ` · ${c.platform_code}` : ''}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={STATUS_VARIANT[c.status] || 'idle'}>{c.status}</Badge>
                    </TableCell>
                    <TableCell style={{ textAlign: 'right',
                                        color: c.low_balance ? 'var(--critical)' : undefined }}>
                      {num(c.credits_balance || 0, { decimals: 2 })}
                    </TableCell>
                    <TableCell style={{ textAlign: 'right' }}>{num(c.requests || 0)}</TableCell>
                    <TableCell style={{ textAlign: 'right',
                                        color: (c.errors || 0) > 0 ? 'var(--critical)' : undefined }}>
                      {num(c.errors || 0)}
                    </TableCell>
                    <TableCell style={{ textAlign: 'right' }}>{num(c.sessions || 0)}</TableCell>
                    <TableCell className="muted">{c.last_active || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {detailId && (
        <ClientDetail clientId={detailId}
                      onClose={() => { setOpen(null); onCloseClient?.() }}
                      onChanged={() => setNonce((n) => n + 1)} />
      )}
      {onboarding && (
        <OnboardModal onClose={() => setOnboarding(false)}
                      onCreated={(id) => { setOnboarding(false); setNonce((n) => n + 1); setOpen(id) }} />
      )}
    </div>
  )
}
